import DragModal from "../../common/components/DragModal";
import {
  DialogContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useEffect, useState, useCallback } from "react";
import { getTagListHistory } from "../../api/APIHelpers";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";

// Fields to skip when computing diffs (internal IDs, audit metadata, and non-user-visible fields)
const SKIP_FIELDS = new Set([
  "TCTagListId",
  "TCTagLayerId",
  "TCTagListAuditId",
  "TCTagLayerAuditId",
  "TCTagListTagAuditId",
  "TCTagListTagId",
  "AuditAction",
  "ActionDate",
  "ActionBy",
  "Tags",
  "TagLayers",
  "IsDirty",
  "Parents",
  "UpdatedDate",
  "UpdatedBy",
  "InsertedDate",
  "InsertedBy",
]);

const TAG_LIST_FIELDS = [
  "Name",
  "Description",
  "HardwareCategory",
  "IsActive",
  "Version",
  "IsTagStandard",
  "AssetTypeId",
  "TenantId",
];

/** Compare two consecutive audit snapshots and return list of { field, oldValue, newValue } */
const computeDiff = (older, newer, skipFields = SKIP_FIELDS) => {
  const changes = [];
  const allKeys = new Set([
    ...Object.keys(older || {}),
    ...Object.keys(newer || {}),
  ]);
  for (const key of allKeys) {
    if (skipFields.has(key)) continue;
    const oldVal = older?.[key] ?? null;
    const newVal = newer?.[key] ?? null;
    if (String(oldVal) !== String(newVal)) {
      changes.push({
        field: key,
        oldValue: formatValue(oldVal),
        newValue: formatValue(newVal),
      });
    }
  }
  return changes;
};

const formatValue = (val) => {
  if (val === null || val === undefined) return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  return String(val);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleString();
};

/**
 * Build a unified, sorted timeline from the three audit collections returned by the API.
 * Returns array of entry objects shaped for rendering.
 */
const buildTimeline = (data) => {
  const entries = [];

  // --- Tag List audits ---
  const tagListAudits = [...(data.tagListHistory || [])].sort(
    (a, b) => new Date(a.ActionDate) - new Date(b.ActionDate)
  );
  tagListAudits.forEach((audit, idx) => {
    const prev = idx > 0 ? tagListAudits[idx - 1] : null;
    const action = audit.AuditAction;
    let changes = [];
    if (action === "Update" && prev) {
      changes = computeDiff(prev, audit, SKIP_FIELDS);
    } else if (action === "Insert" || (action === "Update" && !prev)) {
      // Show non-null fields as initial/recorded values (no previous record to diff against)
      changes = TAG_LIST_FIELDS.filter((f) => audit[f] != null).map((f) => ({
        field: f,
        oldValue: null,
        newValue: formatValue(audit[f]),
      }));
    }
    entries.push({
      id: `tl-${audit.TCTagListAuditId ?? idx}`,
      date: audit.ActionDate,
      user: audit.UpdatedBy,
      type: "Tag List",
      action,
      summary: buildTagListSummary(action, changes),
      changes,
    });
  });

  // --- Tag Membership audits ---
  (data.tagListTagHistory || []).forEach((audit, idx) => {
    // Try to resolve a human-readable tag name from tagLayerHistory
    const layerAudits = data.tagLayerHistory?.[audit.TCTagLayerId];
    const tagName =
      layerAudits?.find((l) => l.TagName)?.TagName ??
      `Tag Layer #${audit.TCTagLayerId}`;
    const action = audit.AuditAction;
    const verb = action === "Delete" ? "removed from" : "added to";
    entries.push({
      id: `tm-${audit.TCTagListTagAuditId ?? idx}`,
      date: audit.ActionDate,
      user: audit.UpdatedBy,
      type: "Tag Membership",
      action,
      summary: `"${tagName}" ${verb} list`,
      changes: [],
    });
  });

  // --- Tag Layer (property) audits ---
  const layerHistory = data.tagLayerHistory ?? {};
  Object.entries(layerHistory).forEach(([layerId, audits]) => {
    const sorted = [...audits].sort(
      (a, b) => new Date(a.ActionDate) - new Date(b.ActionDate)
    );
    sorted.forEach((audit, idx) => {
      const prev = idx > 0 ? sorted[idx - 1] : null;
      const action = audit.AuditAction;
      let changes = [];
      if (action === "Update" && prev) {
        changes = computeDiff(prev, audit, SKIP_FIELDS);
      } else if (action === "Insert" || (action === "Update" && !prev)) {
        // Show non-null TagLayerDTO fields as initial/recorded values
        changes = Object.entries(audit)
          .filter(([k, v]) => !SKIP_FIELDS.has(k) && v != null)
          .map(([k, v]) => ({
            field: k,
            oldValue: null,
            newValue: formatValue(v),
          }));
      }
      const tagName = audit.TagName ?? `Tag Layer #${layerId}`;
      entries.push({
        id: `tp-${audit.TCTagLayerAuditId ?? `${layerId}-${idx}`}`,
        date: audit.ActionDate,
        user: audit.UpdatedBy,
        type: "Tag Property",
        action,
        summary: `"${tagName}" properties ${action.toLowerCase()}`,
        changes,
      });
    });
  });

  // Sort descending (most recent first)
  entries.sort((a, b) => new Date(b.date) - new Date(a.date));
  return entries;
};

const buildTagListSummary = (action, changes) => {
  if (changes.length === 0) return `Tag list ${action.toLowerCase()}`;
  const fields = changes.map((c) => c.field).join(", ");
  return `Changed: ${fields}`;
};

const TYPE_CHIP_COLORS = {
  "Tag List": "primary",
  "Tag Membership": "success",
  "Tag Property": "warning",
};

const ChangeDetails = ({ changes, action }) => {
  return (
    <Table size='small' sx={{ mt: 0.5 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>Property</TableCell>
          {action !== "Insert" && (
            <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>
              Old Value
            </TableCell>
          )}
          <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>New Value</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {changes.map((c) => (
          <TableRow key={c.field}>
            <TableCell sx={{ py: 0.5 }}>{c.field}</TableCell>
            {action !== "Insert" && (
              <TableCell sx={{ py: 0.5, color: "error.main" }}>
                {c.oldValue}
              </TableCell>
            )}
            <TableCell sx={{ py: 0.5, color: "success.main" }}>
              {c.newValue}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const TimelineRow = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = entry.changes.length > 0;

  return (
    <TableRow
      hover={hasDetails}
      onClick={hasDetails ? () => setExpanded((v) => !v) : undefined}
      sx={{ cursor: hasDetails ? "pointer" : "default", verticalAlign: "top" }}>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        {formatDate(entry.date)}
      </TableCell>
      <TableCell>{entry.user || "—"}</TableCell>
      <TableCell>
        <Chip
          label={entry.type}
          color={TYPE_CHIP_COLORS[entry.type] ?? "default"}
          size='small'
        />
      </TableCell>
      <TableCell>{entry.action}</TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant='body2'>{entry.summary}</Typography>
          {hasDetails && (
            <IconButton
              size='small'
              aria-label={expanded ? "collapse details" : "expand details"}
              tabIndex={-1}>
              {expanded ? (
                <CollapseIcon fontSize='small' />
              ) : (
                <ExpandIcon fontSize='small' />
              )}
            </IconButton>
          )}
        </Box>
        {hasDetails && (
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <ChangeDetails changes={entry.changes} action={entry.action} />
          </Collapse>
        )}
      </TableCell>
    </TableRow>
  );
};

export const TagListHistoryModal = ({
  open,
  onClose,
  tagListId,
  tagListName,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeline, setTimeline] = useState([]);

  const loadHistory = useCallback(async () => {
    if (!tagListId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getTagListHistory(tagListId);
      setTimeline(buildTimeline(response.data));
    } catch (err) {
      setError(`Failed to load history: ${extractApiErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, [tagListId]);

  useEffect(() => {
    if (open) {
      loadHistory();
    } else {
      setTimeline([]);
      setError(null);
    }
  }, [open, loadHistory]);

  return (
    <DragModal
      open={open}
      onClose={onClose}
      title='tagListHistory'
      maxWidth='lg'
      fullWidth
      titlecomponent={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}>
          <Typography variant='h6' color='primary'>
            History Log: {tagListName}
          </Typography>
          <IconButton onClick={onClose} size='small' aria-label='close'>
            <CloseIcon />
          </IconButton>
        </Box>
      }>
      <DialogContent sx={{ minHeight: 300 }}>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              mt: 4,
            }}>
            <Alert severity='error' sx={{ width: "100%" }}>
              {error}
            </Alert>
            <Button variant='outlined' onClick={loadHistory}>
              Retry
            </Button>
          </Box>
        )}

        {!loading && !error && timeline.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}>
            <Typography color='text.secondary'>
              No history records found.
            </Typography>
          </Box>
        )}

        {!loading && !error && timeline.length > 0 && (
          <TableContainer component={Paper} variant='outlined'>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>User</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeline.map((entry) => (
                  <TimelineRow key={entry.id} entry={entry} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </DragModal>
  );
};
